/**
 * ชนิดข้อมูลของกฎตรวจรับเข้า (Incoming Inspection Rule) — เขียนตาม payload contract
 * ที่ freeze แล้ว (`docs/API_CONTRACT.md` — "Incoming Inspection Rule — Payload
 * Contract (D-086)")
 *
 * กฎหนึ่งข้อตอบคำถามเดียว: **ของที่รับเข้าจากผู้ขายรายนี้ สำหรับสินค้าตัวนี้ ต้อง
 * กักรอตรวจหรือไม่** — งานรับเข้า (P2-3b) เป็นผู้อ่านคำตอบนั้นตอนของมาถึงจริง
 *
 * สามข้อที่ต้องอ่านออกจากชนิดข้อมูลนี้โดยไม่ต้องเปิดเอกสาร:
 *
 * 1. **ไม่มี `plant_code`/`plant_id` ทั้งขาไปและขากลับ** (D-086) — สินค้าและผู้ขาย
 *    เป็น master ระดับองค์กร กฎที่ผูกทั้งสองเข้าด้วยกันจึงเป็นกฎระดับองค์กรด้วย
 *    ไม่ได้ถูกกรองด้วยขอบเขตโรงงานของผู้เรียก
 * 2. **คู่ (product, supplier) แก้ไม่ได้** — มันคือสิ่งที่ unique constraint จับและ
 *    สิ่งที่การรับเข้าใช้ค้นหา การย้ายกฎไปอีกคู่หนึ่งจะเปลี่ยนคำตอบของสองคู่พร้อมกัน
 *    `UpdateQualityRuleBody` ด้านล่างจึง **ไม่มีคีย์ทั้งสองอยู่ในชนิดข้อมูล** ไม่ใช่
 *    แค่ไม่ส่ง
 * 3. **ไม่มี DELETE โดยตั้งใจ** — ยกเลิกการตรวจคือ `PATCH`
 *    `incoming_inspection_required = false` ซึ่งมีผู้กระทำและมีแถว `audit_log`
 *    ส่วนการลบแถวทิ้งจะทำให้ระบบเงียบ ๆ กลับไปใช้ค่าเริ่มต้น "ต้องตรวจ" โดยไม่มีใคร
 *    รู้ว่าใครเป็นคนเปลี่ยน
 */

/**
 * รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — **`INVENTORY` เดิม ไม่ใช่โมดูลใหม่ชื่อ
 * `QUALITY`** (D-086)
 *
 * กฎนี้กำหนดสถานะคุณภาพของสต๊อกที่กำลังจะรับเข้า จึงเป็นสิทธิ์ชุดเดียวกับที่คน
 * คลังและคนรับของถืออยู่แล้ว การประกาศโมดูลที่สองตรงนี้จะได้หน้าจอที่ไม่มีแผนกไหน
 * ถือได้เลย จนกว่าจะมีใครไปเพิ่มแถวในตารางสิทธิ์ให้
 *
 * ประกาศซ้ำไว้ในโดเมนของตัวเองด้วยเหตุผลเดียวกับ `lib/lotTraceability/types.ts`
 * ซึ่งก็ประกาศค่าเดียวกันนี้แทนการ import ข้ามโดเมน — ทั้งสองหน้าจอผูกกับโมดูล
 * เดียวกันโดยบังเอิญของ RBAC ไม่ใช่เพราะเป็นโดเมนเดียวกัน
 *
 * เป็นโมดูล **ธุรกิจ** ปกติ ไม่ใช่กลุ่ม System Administration — ADMIN ถูกปฏิเสธ
 * ตาม D-060
 */
export const INVENTORY_MODULE = 'INVENTORY';

/* ------------------------------------------------------- ข้อจำกัดความยาว -- */

/**
 * `note` ≤ 255 — ค่าเดียวที่ contract ของกลุ่มนี้ระบุความยาวไว้ตรง ๆ
 *
 * ⚠️ **ไม่มีค่าคู่กันสำหรับ `product_code`/`supplier_code` โดยตั้งใจ** — contract ของ
 * กลุ่มนี้ไม่ได้ระบุความยาวของทั้งสองไว้ และการเดาตัวเลขมาใส่ `maxLength` จะทำให้
 * หน้าจอ **ตัดตัวอักษรทิ้งเงียบ ๆ** จนผู้ใช้ส่งรหัสที่ไม่ใช่รหัสที่เขาพิมพ์ ถ้ารหัสยาว
 * เกินคอลัมน์จริง เซิร์ฟเวอร์เป็นผู้ตอบ และหน้าจอแสดงคำตอบนั้นตามที่มันเป็น
 */
export const QUALITY_RULE_NOTE_MAX_LENGTH = 255;

/* ------------------------------------------------------------- กฎธุรกิจ --- */

/**
 * `409` เมื่อคู่ (product, supplier) นี้มีกฎอยู่แล้ว — **ไม่เขียนอะไรเลย**
 *
 * ทางแก้อยู่ในมือผู้ใช้ทันที: กฎของคู่นี้มีอยู่แล้วและแก้ได้จากรายการ ไม่ใช่สร้างใหม่
 * ทับของเดิม การยุบมันเข้ากับ `server-error` คือการส่งเขาไปโทรหาฝ่ายไอที
 */
export const QUALITY_RULE_EXISTS = 'QUALITY_RULE_EXISTS';

/**
 * `422` เมื่อ `product_code` ที่กรอกไม่มีอยู่ในระบบ
 *
 * เป็น `422` **ไม่ใช่ `404`** โดยตั้งใจของ contract: resource ที่ถูกเรียกคือ *กฎ*
 * ส่วนรหัสสินค้าเป็นเนื้อหาของ payload — `404` จะอ่านว่า "ไม่มีกฎข้อนี้" ซึ่งคนละ
 * เรื่องกับ "ไม่มีสินค้ารหัสนี้"
 */
export const PRODUCT_NOT_FOUND = 'PRODUCT_NOT_FOUND';

/** `422` เมื่อ `supplier_code` ที่กรอกไม่มีอยู่ในระบบ — เหตุผลเดียวกับข้างบน */
export const SUPPLIER_NOT_FOUND = 'SUPPLIER_NOT_FOUND';

/**
 * `400` เมื่อ body พา field ที่เซิร์ฟเวอร์เป็นเจ้าของไปด้วย (`rule_id`, `product_id`,
 * `supplier_id`, `created_by_employee_id`, `created_at`, `updated_at`)
 *
 * หน้าจอนี้ไม่มีเส้นทางที่ประกอบคีย์เหล่านั้นเข้า body ได้เลย การที่มันเกิดขึ้นจริง
 * จึงแปลว่าหน้าจอกับ API ไม่ตรงกันแล้ว — เป็นบั๊กที่ต้องเห็น ไม่ใช่ความล้มเหลวที่
 * กลืนเงียบ ๆ
 */
export const QUALITY_RULE_FIELD_IS_SERVER_OWNED = 'QUALITY_RULE_FIELD_IS_SERVER_OWNED';

/**
 * `400` เมื่อ body ของ `PATCH` มี `product_code` หรือ `supplier_code` ติดไปด้วย
 *
 * ด้วยเหตุผลเดียวกับข้อข้างบน: `UpdateQualityRuleBody` ไม่มีคีย์ทั้งสองอยู่ในชนิด
 * ข้อมูล และกล่องแก้ไขแสดงคู่นี้เป็นข้อความอ่านอย่างเดียว
 */
export const QUALITY_RULE_PAIR_IS_IMMUTABLE = 'QUALITY_RULE_PAIR_IS_IMMUTABLE';

/**
 * คำตอบของระบบเมื่อ **ไม่มีกฎ** สำหรับคู่นั้น — "ต้องตรวจ" (D-059/D-080)
 *
 * เป็นค่าฝั่งปลอดภัย และเป็นเหตุผลที่ `incoming_inspection_required` **บังคับและ
 * ไม่มี default บนสาย**: กฎที่เขียนลงไปโดยไม่ระบุคำตอบคือกฎที่ไม่ได้บอกอะไรเพิ่ม
 * จากค่าเริ่มต้นเลย และที่แย่กว่าคือคนเขียนอาจเข้าใจว่าได้ยกเว้นการตรวจไปแล้ว
 */
export const DEFAULT_INCOMING_INSPECTION_REQUIRED = true;

/* ------------------------------------------------------------- resource --- */

/** กฎหนึ่งข้อตามที่ contract ระบุ — สิบฟิลด์ และ **ไม่มีฟิลด์ที่เอ่ยถึงโรงงานเลย** */
export interface ProductSupplierQualityRule {
  /** UUID surrogate ที่เซิร์ฟเวอร์สร้าง — ใช้เป็น path ของ `GET`/`PATCH` รายรายการ */
  readonly rule_id: string;
  readonly product_id: string;
  readonly product_code: string;
  readonly supplier_id: string;
  readonly supplier_code: string;
  readonly incoming_inspection_required: boolean;
  /** `null` = ไม่มีหมายเหตุ ต่างจากสตริงว่างที่เป็นหมายเหตุที่ว่างเปล่า */
  readonly note: string | null;
  readonly created_by_employee_id: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/product-supplier-quality-rules` — envelope มาตรฐานของทุกกลุ่ม */
export interface QualityRuleListPage {
  readonly items: readonly ProductSupplierQualityRule[];
  readonly next_cursor: string | null;
}

/* -------------------------------------------------------------- requests -- */

/**
 * body ของ `POST /v1/product-supplier-quality-rules`
 *
 * `incoming_inspection_required` เป็น **`boolean` ที่ไม่ optional** — ชนิดข้อมูลเป็น
 * ด่านแรกของกฎ "ไม่มี default" ผู้เรียกที่ยังไม่ได้ตัดสินใจจะประกอบ body นี้ไม่ได้เลย
 *
 * `note` เป็น optional จริง ๆ: **ไม่ส่งเลย** ต่างจากส่งสตริงว่าง
 */
export interface CreateQualityRuleBody {
  readonly product_code: string;
  readonly supplier_code: string;
  readonly incoming_inspection_required: boolean;
  readonly note?: string | null;
}

/**
 * body ของ `PATCH /v1/product-supplier-quality-rules/{rule_id}` — **ไม่มีคู่ code**
 *
 * ทั้งสองคีย์เป็น optional เพราะ `PATCH` พาไปเฉพาะสิ่งที่เปลี่ยน คีย์ที่ไม่ได้ส่งถูก
 * ปล่อยไว้ตามเดิม (เซิร์ฟเวอร์อ่านจาก `model_fields_set` ไม่ใช่จากค่า `null`)
 *
 * `note: null` คือ **การล้างหมายเหตุ** ซึ่งเป็นคำสั่งที่ส่งได้จริง ส่วน
 * `incoming_inspection_required` ส่ง `null` ไม่ได้เพราะคอลัมน์เป็น `NOT NULL` —
 * ชนิดข้อมูลตรงนี้จึงเป็น `boolean` ไม่ใช่ `boolean | null`
 */
export interface UpdateQualityRuleBody {
  readonly incoming_inspection_required?: boolean;
  readonly note?: string | null;
}
